import type { WorkingRights } from '../../../../schemas/teacherReg'
import { Stack } from '@mui/material'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { UserRegSx } from '../../../../components/TextField'
import pxToRem from '../../../../helpers/pxToRem'
// import FileUpload from "./FileUpload";
import FileUpload, { FileType } from '../../../../components/FileUpload'
import styles from '../index.module.scss'
import { RegModalWrapper } from '../ModalContent'
import { documentUploadHandler } from './helper'
import { setErrorMsg } from '../../../../redux/uiSlice'
import { RegFileUploadLabel } from '../../../../components/FileUpload/RegUploadComponents'
import { updateUserInfo } from '../../../../firebase/general-apis'
import { workingRightsTypes } from '../../../../schemas/teacherReg'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import FormSelect from '../../../../components/FormComponents/FormSelect'
import {
    selectTeacherPrimaryIdentityId,
    selectTeacherWorkingRights,
} from '../../../../redux/teacherSlice'
import { selectUserUid } from '../../../../redux/userSlice'
import { logger } from '../../../../helpers/logger'
import { FileUploadSchema } from '../../../../schemas/helper'
import z from 'zod'

const formSchema = z
    .object({
        value: workingRightsTypes,
        document: FileUploadSchema.optional(),
    })
    .check((ctx) => {
        if (
            ctx.value.value === 'temporaryVisaHolder' &&
            ctx.value.document === undefined
        ) {
            ctx.issues.push({
                code: 'custom',
                message: 'Support document required',
                input: ctx.value.document,
                path: ['document'],
            })
        }
    })
type FormType = z.infer<typeof formSchema>
// const formSchema = workingRightsSchema.check((ctx) => {
//     if (ctx.value.value === 'temporaryVisaHolder' && !ctx.value.url) {
//         ctx.issues.push({
//             code: 'custom',
//             message: 'Supporting document required',
//             input: ctx.value.url,
//             path: ['url'],
//         })
//     }
// })

export const WorkingRightsWrapper = () => {
    const workingRights = useSelector(selectTeacherWorkingRights)
    const primaryDocId = useSelector(selectTeacherPrimaryIdentityId)

    if (workingRights.status === 'loading' || primaryDocId.loading) {
        return null
    }

    if (workingRights.status === 'default') {
        return 'Could not fetch data'
    }
    if (workingRights.status === 'error') {
        return 'Could not fetch data'
    }

    if (workingRights.status === 'success') {
        return (
            <WorkingRights
                workingRights={workingRights.data}
                primaryDocId={primaryDocId.data}
            />
        )
    }
    return null
}

// = ["Australian Citizen", "Permanent Resident", "Temporary Working Visa"];
const WorkingRights = ({
    workingRights,
    primaryDocId,
}: {
    workingRights: WorkingRights
    primaryDocId: string | undefined
}) => {
    const dispatch = useDispatch()
    const userId = useSelector(selectUserUid)

    const {
        control,
        watch,
        setValue,
        handleSubmit,
        formState: { isValid, errors, isDirty },
    } = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: {
            value: workingRights.value || undefined,
            document: workingRights.url || undefined,
        },
    })
    const type = watch('value')

    useEffect(() => {
        logger.debug(workingRights)
    }, [workingRights])

    const [file, setFile] = useState<FileType | undefined>(
        typeof workingRights.url === 'string'
            ? {
                  file: workingRights.url,
                  fileName: workingRights.docTitle || '',
              }
            : undefined
    )

    const onFileChange = (file: any) => {
        let f =
            file instanceof File
                ? file
                : new File([file], file.name, { type: file.type })
        setFile({ file: file, fileName: file.name })
        setValue('document', f, {
            shouldDirty: true,
            shouldValidate: true,
        })
    }

    const handleUpdate = async (data: FormType) => {
        try {
            if (!isDirty || data.value === null) {
                return
            }

            if (!userId) {
                throw new Error(
                    'User is not authenticated. Please refresh and try again.'
                )
            }

            let toUpdate: Record<string, any> = {}

            if (data.value === 'temporaryVisaHolder') {
                // const file: FileType =
                //     typeof data.url === 'string'
                //         ? { file: data.url }
                //         : data.url !== null
                //           ? { file: data.url, fileName: data.url?.name }
                //           : undefined

                const { fileUrl, fileType, docTitle } =
                    await documentUploadHandler(userId, file, {
                        url: workingRights.url,
                        type: workingRights.type,
                        docTitle: workingRights.docTitle,
                    })

                toUpdate['url'] = fileUrl
                toUpdate['type'] = fileType
                toUpdate['docTitle'] = docTitle
            }

            toUpdate['state'] = 'submitted'
            toUpdate['value'] = data.value

            // Clean any primary document that is not a passport if the user selects temporary resident
            if (data.value === 'temporaryVisaHolder' && !!primaryDocId) {
                await updateUserInfo(userId, {
                    teacher: {
                        path: '/identity',
                        data: {
                            [`${primaryDocId}`]: null,
                        },
                    },
                })
            }

            await updateUserInfo(userId, {
                teacher: {
                    path: '/workingRights',
                    data: toUpdate,
                },
            })
        } catch (error: any) {
            dispatch(setErrorMsg(error.message))
        }
    }

    return (
        <>
            <RegModalWrapper
                title="Working Rights"
                handleUpdate={handleSubmit(handleUpdate)}
                state={workingRights.state}
                isAllComplete={isValid}
            >
                <Stack gap={pxToRem(40)} className={styles.modalContainer}>
                    <Stack className={styles.titleContainer}>
                        <h5 className={styles.h5}>Working Rights</h5>
                        <p className={styles.p}>
                            Select your Australian working rights
                        </p>
                    </Stack>
                    <Stack gap={pxToRem(24)}>
                        <FormSelect
                            control={control}
                            name="value"
                            variant="standard"
                            props={{
                                label: 'Working Rights',
                            }}
                            controlProps={{
                                sx: UserRegSx,
                            }}
                            options={[
                                {
                                    label: 'Australian Citizen',
                                    value: 'unrestrictedCitizen',
                                },
                                {
                                    label: 'Permanent Resident',
                                    value: 'unrestrictedPermanentResident',
                                },
                                {
                                    label: 'Temporary Working Visa',
                                    value: 'temporaryVisaHolder',
                                },
                            ]}
                        />
                        {type !== undefined &&
                        type === 'temporaryVisaHolder' ? (
                            <>
                                <RegFileUploadLabel
                                    file={file}
                                    reset={() => {
                                        setFile(undefined)
                                        setValue('document', undefined, {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                            shouldTouch: true,
                                        })
                                    }}
                                />
                                <FileUpload
                                    file={file}
                                    onChangeCallback={onFileChange}
                                    extensions={'document'}
                                    iconColor={styles.primaryYellow500}
                                />
                            </>
                        ) : null}
                    </Stack>
                </Stack>
            </RegModalWrapper>
        </>
    )
}

// export default WorkingRights
